import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";

import "animate.css/animate.min.css";
import { SetProfilePictureModalIsShown } from "../../AppMenu/Store";
import ProfilePicture from "../../SharedComponents/ProfilePicture";
import IconSvg from "../../SharedComponents/IconSvg";
import { logOut } from "../../../Helpers/AuthHelper";

export default function ProfileDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const reduxProfilePicture = useSelector((s) => s.appMenu.reduxProfilePicture);
  const user = useSelector((s) => s.auth?.user);

  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isEditHovered, setIsEditHovered] = useState(false);
  const wrapperRef = useRef(null);

  // open immediately
  const openMenu = () => {
    setIsClosing(false);
    setIsOpen(true);
  };
  // trigger fade-out
  const closeMenu = () => {
    setIsClosing(true);
  };
  // toggle on click
  const handleToggle = () => {
    isOpen ? closeMenu() : openMenu();
  };
  // after fade-out ends, actually hide
  const handleAnimationEnd = () => {
    if (isClosing) {
      setIsOpen(false);
      setIsClosing(false);
    }
  };
  // click outside to close
  useEffect(() => {
    const onClickOutside = (e) => {
      if (isOpen && wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);

  const handleLanguageChange = (lang) => i18next.changeLanguage(lang);

  // choose slide-in / slide-out
  const animClass = isClosing ? "animate__fadeOutRight" : "animate__fadeInRight";
  // swap chevron when fully open (not during closing)
  const chevronIcon = isOpen && !isClosing ? "chevron-left" : "chevron-right";

  return (
    <div ref={wrapperRef} style={{ position: "relative", display: "inline-block" }}>
      {/* Toggle */}
      <div className="dropdown-toggle no-caret show-cursor" onClick={handleToggle}>
        <div style={{ width: 80 }}>
          <div
            className="rounded-circle border border-0 border-warning overflow-hidden"
            style={{ height: 60, width: 60, marginLeft: 5 }}
          >
            {reduxProfilePicture ? (
              <Image src={reduxProfilePicture} alt="preview" className="h-100 w-100" />
            ) : (
              <ProfilePicture
                profilePicFileName={user.profilePic}
                googleProfilePic={user.googlePic}
                userId={user.userId}
                size={60}
              />
            )}
          </div>
        </div>
        <div className="text-color-white text-14 fw-normal" style={{ width: 80, height: 20 }}>
          <Row className="justify-content-center h-100 mx-0">
            <Col xs="auto" className="px-0">
              {t("Manage")}
            </Col>
            <Col xs="auto" className="px-0">
              <IconSvg icon={chevronIcon} width={15} height={15} fill="#fff" />
            </Col>
          </Row>
        </div>
      </div>

      {/* Menu */}
      {isOpen && (
        <div
          className={[
            "default-card extra-shadow text-17 fw-medium text-color-gray-dark",
            "animate__animated animate__faster",
            animClass,
          ].join(" ")}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            minWidth: 300,
            borderRadius: "20px 0 20px 20px",
            padding: "15px 0",
            backgroundColor: "#fcfcfc",
            zIndex: 1000,
          }}
          onAnimationEnd={handleAnimationEnd}
        >
          {/* Header */}
          <Row className="mb-2 text-color-gray-dark px-2">
            <Col xs="auto">
              <div className="position-relative">
                <div
                  className="rounded-circle border border-0 border-warning overflow-hidden"
                  style={{ height: 70, width: 70 }}
                >
                  {reduxProfilePicture ? (
                    <Image src={reduxProfilePicture} alt="preview" className="h-100 w-100" />
                  ) : (
                    <ProfilePicture
                      profilePicFileName={user.profilePic}
                      googleProfilePic={user.googlePic}
                      userId={user.userId}
                      size={70}
                    />
                  )}
                </div>
                <div
                  className="position-absolute rounded-circle p-0 bg-transparent"
                  style={{
                    width: 20,
                    height: 20,
                    bottom: 0,
                    right: 0,
                    cursor: "pointer",
                    border: `1px solid ${isEditHovered ? "#274c77" : "#0077b6"}`,
                  }}
                  onMouseEnter={() => setIsEditHovered(true)}
                  onMouseLeave={() => setIsEditHovered(false)}
                  onClick={() => {
                    dispatch(SetProfilePictureModalIsShown(true));
                    closeMenu();
                  }}
                >
                  <IconSvg icon="editV2" width={12} height={12} fill={isEditHovered ? "#274c77" : "#0077b6"} />
                </div>
              </div>
            </Col>
            <Col className="align-self-center">
              <Row className="text-15 px-2">{user?.companyUser?.companyUsername}</Row>
              <Row className="text-color-gray-light text-12 px-2">{user?.companyUser?.company?.companyName}</Row>
            </Col>
          </Row>

          {/* Help */}
          <div className="py-1 px-3 text-14" style={{ borderTop: "1px solid #cfcfcf" }}>
            {t("topBarMenuHelp")}
          </div>
          <div className="custom-dropdown-item px-3">
            <Row className="hover-bg-darken flex-nowrap py-1">
              <Col className="text-16 text-color-gray-light">{t("topBarMenuSiteGuide")}</Col>
            </Row>
          </div>
          <div
            className="custom-dropdown-item px-3"
            onClick={() => navigate(`/${user.userRole === "SuperAdmin" ? "SuperAdmin" : ""}HelpDesk`)}
          >
            <Row className="hover-bg-darken flex-nowrap py-1">
              <Col className="text-16 text-color-gray-light">{t("topBarMenuHelpDesk")}</Col>
            </Row>
          </div>

          {/* Language */}
          <div className="py-1 px-3 text-14" style={{ borderTop: "1px solid #cfcfcf" }}>
            {t("topBarMenuLanguage")}
          </div>
          <div className="custom-dropdown-item px-3" onClick={() => handleLanguageChange("tr")}>
            <Row className="hover-bg-darken flex-nowrap py-1">
              <Col>{t("turkish")}</Col>
              <Col xs="auto">
                <IconSvg icon="flag-tr" height={24} width={24} />
              </Col>
            </Row>
          </div>
          <div className="custom-dropdown-item px-3" onClick={() => handleLanguageChange("en")}>
            <Row className="hover-bg-darken flex-nowrap py-1">
              <Col>{t("english")}</Col>
              <Col xs="auto">
                <IconSvg icon="flag-uk" height={24} width={24} />
              </Col>
            </Row>
          </div>

          {/* Actions */}
          <div className="py-1 px-3 text-14" style={{ borderTop: "1px solid #cfcfcf" }}>
            {t("topBarMenuActions")}
          </div>
          <div className="custom-dropdown-item px-3" onClick={logOut}>
            <Row className="hover-bg-darken flex-nowrap py-1">
              <Col className="text-16 text-color-gray-light">{t("logout")}</Col>
              <Col xs="auto">
                <IconSvg icon="exit" />
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
}
